import CoursePathItem from '@/components/admin/courses/CoursePathItem';
import InfinityList from '@/components/ui/custom/InfinityList';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import useInfiniteService from '@/data/hook/useInfiniteService';
import {
  coursePathsQueryKeys,
  courseLinesQueryKeys,
} from '@/data/services/querykeys';
import { ListCoursePathFilters } from '@/data/services/coursePathServices';
import Text from '../../Text';
import { useTranslation } from 'react-i18next';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import useListService from '@/data/hook/useListService';
import useContentElement from '@/data/hook/useContentElement';
import CoursePath from '@/models/CoursePath';
import { getLineColor } from '@/utils/courseLineColors';
import { CourseLine } from '@/models/CourseLine';

export enum CoursePathGroupByEnum {
  NONE = 'NONE',
  STATUS = 'STATUS',
  LINE = 'LINE',
}

export type CoursePathGroupByProps = {
  groupBy: CoursePathGroupByEnum;
  filters: ListCoursePathFilters;
  handleSelectCourse: (course: CoursePath) => void;
};

type Colors =
  | 'warning'
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'error'
  | 'success'
  | 'neutral'
  | 'base';

export default function CoursePathGroupBy({
  groupBy,
  filters = {},
  handleSelectCourse,
}: CoursePathGroupByProps) {
  const contentElement = useContentElement();
  const [selectedGroup, setSelectedGroup] = useState<string>();
  const [tCourseStatus] = useTranslation('translation', {
    keyPrefix: 'courses.status',
  });

  const { results: courseLines } = useListService({
    ...courseLinesQueryKeys.list({
      pageSize: 50,
    }),
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const statusColor: Record<VersioningStatusEnum, Colors> = {
    BETA: 'warning',
    DISABLED: 'error',
    EDITING: 'secondary',
    PUBLISHED: 'success',
    VERSIONING: 'accent',
  };

  function getGroupFilter(
    value: string,
    courseLine?: CourseLine,
  ): ListCoursePathFilters {
    const groupFilters = { ...filters };
    if (courseLine) {
      groupFilters.courseLineId = [courseLine.id];
    } else if (value in VersioningStatusEnum) {
      groupFilters.status = [value] as VersioningStatusEnum[];
    }
    return groupFilters;
  }

  function getColor(value: string, courseLine?: CourseLine): Colors {
    if (courseLine) {
      return getLineColor(courseLine.name).color as Colors;
    }
    if (value in VersioningStatusEnum) {
      return statusColor[value as VersioningStatusEnum];
    }
    return 'primary';
  }

  function getTitle(value: string, courseLine?: CourseLine): string {
    if (courseLine) {
      return courseLine.name;
    }
    if (value in VersioningStatusEnum) {
      return tCourseStatus(value);
    }
    return value;
  }

  const isGroupedBy = groupBy !== CoursePathGroupByEnum.NONE;

  function getActiveGroups(): { key: string; courseLine?: CourseLine }[] {
    if (groupBy === CoursePathGroupByEnum.LINE) {
      return courseLines.map(line => ({
        key: String(line.id),
        courseLine: line,
      }));
    }
    if (groupBy === CoursePathGroupByEnum.STATUS) {
      return Object.values(VersioningStatusEnum).map(status => ({
        key: status,
      }));
    }
    return [];
  }

  const activeGroups = getActiveGroups();

  function filterDisplayOnly(group: { key: string }) {
    return selectedGroup === undefined || group.key === selectedGroup;
  }

  function onSelectGroup(groupKey: string) {
    const isSelected = groupKey === selectedGroup;
    setSelectedGroup(isSelected ? undefined : groupKey);
    contentElement.current?.scroll({ top: 0, behavior: 'smooth' });
  }

  if (!isGroupedBy) return null;

  return (
    <ul className="flex flex-col gap-2.5">
      {activeGroups.filter(filterDisplayOnly).map(group => (
        <CoursePathGroup
          key={group.key}
          title={getTitle(group.key, group.courseLine)}
          color={getColor(group.key, group.courseLine)}
          filters={getGroupFilter(group.key, group.courseLine)}
          onClick={() => onSelectGroup(group.key)}
          isDisplayOnly={selectedGroup === group.key}
          handleSelectCourse={handleSelectCourse}
        />
      ))}
    </ul>
  );
}

type CoursePathGroupProps = {
  title: string;
  color: Colors;
  filters: ListCoursePathFilters;
  onClick(): void;
  isDisplayOnly?: boolean;
  handleSelectCourse: (course: CoursePath) => void;
};

function CoursePathGroup({
  title,
  color,
  filters,
  onClick,
  isDisplayOnly,
  handleSelectCourse,
}: CoursePathGroupProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'coursesView.admin.groupBy',
  });
  type StyleItems = {
    text: string;
    border: string;
  };
  const styles: Record<Colors, StyleItems> = {
    warning: { text: 'text-warning', border: 'border-warning' },
    error: { text: 'text-error', border: 'border-error' },
    secondary: { text: 'text-secondary', border: 'border-secondary' },
    success: { text: 'text-success', border: 'border-success' },
    accent: { text: 'text-accent', border: 'border-accent' },
    primary: { text: 'text-primary', border: 'border-primary' },
    neutral: { text: 'text-neutral', border: 'border-neutral' },
    base: { text: 'text-base-content', border: 'border-base-300' },
  };
  const groupStyle = styles[color];

  const {
    results: courses,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isSuccess,
    meta,
    isLoading,
  } = useInfiniteService({
    ...coursePathsQueryKeys.list({ ...filters, pageSize: 5 })._ctx.infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });
  const { results: mostRatedCourses } = useListService({
    ...coursePathsQueryKeys.list({
      ...filters,
      ordering: '-rating',
      pageSize: 1,
    }),
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const mostRatedCourse = mostRatedCourses.at(0)?.rating
    ? mostRatedCourses.at(0)
    : undefined;

  const buttonText = isDisplayOnly ? t('back') : t('all');
  const cardClassName = isDisplayOnly ? '' : 'carousel-item w-76';
  const loadingCards = Array.from({ length: 20 }).map((_, index) => (
    <CoursePathItem isLoading key={index} className={cardClassName} />
  ));

  if (isSuccess && meta?.pagination.count === 0) return null;

  return (
    <li
      className={twJoin(
        'flex flex-col p-2.5 gap-2.5',
        !isDisplayOnly && 'border border-neutral-content rounded-lg',
      )}
    >
      <div className="flex justify-between items-center">
        <Text
          text={title}
          className={`text-18 ${groupStyle.text}`}
          format="rubik-500"
        />
        <MainButton
          text={buttonText}
          color="custom"
          className={twJoin(
            'font-600 text-14 text-primary gap-1.5',
            !isDisplayOnly && 'flex-row-reverse',
          )}
          icon={
            <ChevronRightIcon
              className={twJoin(
                'w-4 h-4 ease-in-out transition-all duration-200',
                isDisplayOnly && 'rotate-180',
              )}
            />
          }
          onClick={onClick}
        />
      </div>
      <InfinityList
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onReachEnd={fetchNextPage}
        className={
          isDisplayOnly
            ? 'grid grid-cols-2 md:grid-cols-4 gap-2.5'
            : 'flex-row carousel carousel-center max-w-full space-x-4'
        }
      >
        <ConditionalRenderer condition={!isLoading} fallback={loadingCards}>
          {courses.map(coursePath => (
            <CoursePathItem
              coursePath={coursePath}
              key={coursePath.id}
              className={twJoin(
                cardClassName,
                mostRatedCourse?.id === coursePath.id &&
                  `border ${groupStyle.border}`,
              )}
              handleSelectCourse={handleSelectCourse}
            />
          ))}
        </ConditionalRenderer>
      </InfinityList>
    </li>
  );
}
